// =====================================================================
// SiteNav, shared top-of-page navigation.
// ---------------------------------------------------------------------
// Two coordinated bars, swapped by viewport via CSS:
//   - <header className="mobile-nav">  shown <= 900px wide, sticks
//     to the top of the page (NOT floating, sits in the document).
//     Uses a hamburger toggle to disclose the link list so every
//     destination from the desktop nav survives onto mobile (the
//     previous "progressively hide labels at 540px / 420px" approach
//     lost Pricing + Playground + Cost + GitHub + Paper on the most
//     common phone widths).
//   - <div className="nav-wrap"> + <header className="site">  shown
//     > 900px wide, floats as a pill at top:18px and collapses to
//     a tighter pill once the user scrolls past 24px.
//
// Both bars carry the same affordances: wordmark, Pricing,
// Playground, Cost, GitHub, Paper, signed-in email (if any),
// theme toggle, and the primary CTA (Sign in / Open dashboard /
// Open admin).
//
// Props:
//   alwaysScrolled, optional. Forces the `.scrolled` class on the
//     desktop floating pill, regardless of window.scrollY. Used by
//     /paper where the parent window does not scroll (the embedded
//     iframe does), so the natural scroll listener would never
//     fire, leaving the pill in its transparent unscrolled state
//     hovering invisibly over the paper content.
//
// Drop this component at the top of any page that wants the site
// chrome; the CSS rules already live in src/styles/landing.css.
// =====================================================================

import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import ThemeToggle from "./ThemeToggle.jsx";

const GH_REPO = "https://github.com/Jamie2111/liquid-memory";

export default function SiteNav({ alwaysScrolled = false }) {
  const navWrap = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, isAdmin, profile, user } = useAuth();
  const navEmail = isAuthenticated
    ? profile?.email ?? user?.email ?? null
    : null;

  const navCtaTo = isAuthenticated
    ? isAdmin
      ? "/admin"
      : "/portal"
    : "/login?mode=signin";
  const navCtaLabel = isAuthenticated
    ? isAdmin
      ? "Open admin"
      : "Open dashboard"
    : "Sign in";

  useEffect(() => {
    if (!navWrap.current) return;

    // When alwaysScrolled is set we lock the pill in its "scrolled"
    // visual (white pill, blur, shadow) and skip the scroll listener
    // entirely. This is for pages whose parent window does not scroll.
    if (alwaysScrolled) {
      navWrap.current.classList.add("scrolled");
      return;
    }

    const onScroll = () => {
      if (!navWrap.current) return;
      navWrap.current.classList.toggle("scrolled", window.scrollY > 24);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [alwaysScrolled]);

  // Auto-close the mobile menu whenever the route changes (e.g., the
  // visitor tapped a link in the open drawer). Without this, the
  // drawer stays open over the new page and the visitor has to dismiss
  // it manually before they can interact with the content below.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Esc closes the menu (keyboard a11y; touch users tap the X).
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  // Lock body scroll while the drawer is open so swipe-to-scroll on
  // mobile doesn't move the page underneath the drawer. Uses the
  // position:fixed pattern rather than overflow:hidden because iOS
  // Safari ignores body overflow:hidden (the page would still scroll
  // behind the drawer), and this way the scroll position is restored
  // exactly on close.
  useEffect(() => {
    if (!menuOpen) return;
    const y = window.scrollY;
    const b = document.body.style;
    const prev = { position: b.position, top: b.top, left: b.left, right: b.right, overflow: b.overflow };
    b.position = "fixed";
    b.top = `-${y}px`;
    b.left = "0";
    b.right = "0";
    b.overflow = "hidden";
    return () => {
      b.position = prev.position;
      b.top = prev.top;
      b.left = prev.left;
      b.right = prev.right;
      b.overflow = prev.overflow;
      window.scrollTo(0, y);
    };
  }, [menuOpen]);

  // The wordmark always lands the visitor at the very top of the
  // landing page. Route changes are handled by App's ScrollToTop;
  // this covers the already-on-/ case, where the pathname doesn't
  // change and react-router therefore doesn't re-navigate.
  const goHomeTop = () => {
    setMenuOpen(false);
    if (location.pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // The link list is rendered twice (in the always-visible mobile
  // top bar AND the drawer). DRY-ing it via an array means the two
  // surfaces can't drift apart.
  const NAV_LINKS = [
    { kind: "route", to: "/pricing",       label: "Pricing" },
    { kind: "route", to: "/playground",    label: "Playground" },
    { kind: "route", to: "/benchmarks",    label: "Benchmarks" },
    { kind: "external", href: GH_REPO,     label: "GitHub" },
  ];

  const renderLink = (item, className) => {
    if (item.kind === "external") {
      return (
        <a
          key={item.label}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
        >
          {item.label}
        </a>
      );
    }
    return (
      <Link key={item.label} to={item.to} className={className}>
        {item.label}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile sticky bar, shown <= 900px. */}
      <header className={"mobile-nav" + (menuOpen ? " is-menu-open" : "")}>
        <Link
          to="/"
          className="wordmark mobile-nav-wordmark"
          aria-label="Liquid Memory, home"
          onClick={goHomeTop}
        >
          <img src="/icon.svg" className="wordmark-mark" alt="" width="22" height="22" />
          <span className="mobile-nav-wordmark-text">Liquid Memory</span>
        </Link>

        {/* Right side of the bar: theme toggle, primary CTA, and the
            hamburger that opens the full link drawer. The CTA stays
            visible at all times because it's the highest-value action
            on the bar; everything else hides behind the hamburger so
            the drawer can carry every destination without an overflow
            fight on narrow phones. */}
        <div className="mobile-nav-right">
          <ThemeToggle variant="compact" />
          <Link to={navCtaTo} className="btn btn-primary mobile-nav-cta">
            {navCtaLabel}
          </Link>
          <button
            type="button"
            className={"mobile-nav-burger" + (menuOpen ? " is-open" : "")}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav-drawer"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="mobile-nav-burger-bar" aria-hidden="true" />
            <span className="mobile-nav-burger-bar" aria-hidden="true" />
            <span className="mobile-nav-burger-bar" aria-hidden="true" />
          </button>
        </div>
      </header>

      {/* Mobile drawer + scrim. Rendered always so CSS can transition
          opacity/transform smoothly; aria-hidden + pointer-events:none
          hide it from assistive tech + the pointer when closed. */}
      <div
        className={"mobile-nav-scrim" + (menuOpen ? " is-open" : "")}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />
      <nav
        id="mobile-nav-drawer"
        className={"mobile-nav-drawer" + (menuOpen ? " is-open" : "")}
        aria-hidden={!menuOpen}
      >
        <div className="mobile-nav-drawer-header">
          <span className="mobile-nav-drawer-label">Menu</span>
        </div>
        <ul className="mobile-nav-drawer-list">
          {NAV_LINKS.map((item) => (
            <li key={item.label}>
              {renderLink(item, "mobile-nav-drawer-link")}
            </li>
          ))}
        </ul>
        {navEmail && (
          <div className="mobile-nav-drawer-email mono" title={navEmail}>
            Signed in as {navEmail}
          </div>
        )}
      </nav>

      {/* Desktop floating pill, shown > 900px. */}
      <div className="nav-wrap" ref={navWrap}>
        <header className="site">
          <Link to="/" className="wordmark" aria-label="Liquid Memory, home" onClick={goHomeTop}>
            <img src="/icon.svg" className="wordmark-mark" alt="" width="22" height="22" />
            Liquid Memory
            <span className="vol">v2.3.1</span>
          </Link>
          <nav className="top">
            {NAV_LINKS.map((item) => renderLink(item, ""))}
            {navEmail && (
              <span className="nav-email mono" title={navEmail}>
                {navEmail}
              </span>
            )}
            <ThemeToggle />
            <Link to={navCtaTo} className="btn btn-primary">
              {navCtaLabel} <span className="arr" aria-hidden="true">&rarr;</span>
            </Link>
          </nav>
        </header>
      </div>
    </>
  );
}
